import { Box, Button, Center, Container, Group, Stack, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { Contract } from '@/api/generated/models';
import { useListContracts } from '@/api/generated/contracts/contracts';
import { useSearchInvoices } from '@/api/generated/invoices/invoices';
import { InvoiceStatus } from '@/api/generated/models/invoiceStatus';
import { useListObjects } from '@/api/generated/objects/objects';
import { useListOrganisationConnections } from '@/api/generated/organisation-connections/organisation-connections';
import {
  CardSection,
  fitColumnWidth,
  ListPagination,
  MobileFab,
  MobileListFilters,
  PageHeading,
  RowActionsMenu,
  SearchField,
  SelectField,
  StatusBanner,
  StatusPill,
  TABLE_ACTION_COL_WIDTH,
  TABLE_STATUS_COL_WIDTH,
  tableCenteredCellStyle,
  tableMinWidth,
  tableRowStyle
} from '@/components/common';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import { CreateInvoiceModal } from '@/features/invoices/components/CreateInvoiceModal';
import { statusConfig } from '@/features/invoices/invoiceDisplay';
import { formatObjectLabel } from '@/features/price-lists/objectDisplay';
import { formatAmount, formatDate, otherPartyName } from '@/utils/format';
import { openBinaryInNewTab } from '@/utils/openBinaryInNewTab';
import { usePageSize } from '@/utils/usePageSize';
import { useSavedMessage } from '@/utils/useSavedMessage';
import { useUrlNullableParam, useUrlPageParam, useUrlTextParam } from '@/utils/useUrlFilters';

const handlePreviewInvoice = (invoiceId?: string) => {
  if (!invoiceId) return;
  void openBinaryInNewTab(`/api/v1/invoices/${invoiceId}/pdf`);
};

/** Šire od TABLE_DATE_COL_WIDTH — duže oznake u headeru („DATUM PLAĆANJA”). */
const INVOICE_DATE_COL_WIDTH = 104;

/** Bez PDV-a: samo ukupan iznos (boldano). S PDV-om: ukupan iznos (boldano), PDV u
 * zagradi i iznos bez PDV-a ispod — isti princip kao showVat na ponudi/ugovoru, samo
 * stogovno u jednoj ćeliji umjesto tri odvojena stupca. */
const InvoiceAmountCell = ({
  subtotal,
  taxAmount,
  totalAmount,
  align = 'left'
}: {
  subtotal?: number;
  taxAmount?: number;
  totalAmount?: number;
  align?: 'left' | 'right';
}) => {
  const hasVat = !!taxAmount && taxAmount > 0;

  if (!hasVat) {
    return (
      <Text fz={13.5} fw={700} ta={align} style={{ color: 'var(--color-text-primary)' }}>
        {formatAmount(totalAmount)}
      </Text>
    );
  }

  return (
    <Stack gap={1}>
      <Text fz={13} fw={700} ta={align} style={{ color: 'var(--color-text-primary)' }}>
        {formatAmount(totalAmount)}
      </Text>
      <Text fz={13} ta={align} style={{ color: 'var(--color-text-secondary)' }}>
        ({formatAmount(taxAmount)})
      </Text>
      <Text fz={13} ta={align} style={{ color: 'var(--color-text-secondary)' }}>
        {formatAmount(subtotal)}
      </Text>
    </Stack>
  );
};

const HeaderIntro = () => {
  const { t } = useTranslation();
  return <PageHeading title={t('invoices.title')} subtitle={t('invoices.subtitle')} />;
};

export const InvoicesPage = () => {
  const { t } = useTranslation();
  const [savedMessage, setSavedMessage] = useSavedMessage();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [statusFilter, setStatusFilter] = useUrlTextParam('status', 'ALL');
  const [objectFilter, setObjectFilter] = useUrlNullableParam('objectId');
  const [counterpartyFilter, setCounterpartyFilter] = useUrlNullableParam('counterpartyOrgId');
  const [search, setSearch] = useUrlTextParam('search');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useUrlPageParam();
  const [pageSize, setPageSize] = usePageSize();
  const [createModalOpened, setCreateModalOpened] = useState(false);

  // page dolazi iz URL-a (useUrlPageParam) — na inicijalnom mountu (npr. povratak
  // s detalja preko BackButton-a) NE smije se resetirati na 1, samo kad korisnik
  // stvarno promijeni neki filter nakon toga.
  const isFirstFilterEffect = useRef(true);
  useEffect(() => {
    if (isFirstFilterEffect.current) {
      isFirstFilterEffect.current = false;
      return;
    }
    setPage(1);
  }, [debouncedSearch, statusFilter, objectFilter, counterpartyFilter, pageSize]);

  // refetchOnMount: 'always' — lista mora biti svježa svaki put kad se uđe na
  // ekran, ne samo kad je staleTime istekao (npr. netom kreirana faktura mora
  // odmah biti vidljiva, ne tek za 5 min).
  const { data, isLoading } = useSearchInvoices(
    {
      page,
      pageSize,
      search: debouncedSearch.trim() || undefined,
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      objectId: objectFilter ?? undefined,
      counterpartyOrgId: counterpartyFilter ?? undefined
    },
    { query: { refetchOnMount: 'always' } }
  );
  const pageItems = data?.content ?? [];
  const numberColWidth = useMemo(
    () =>
      fitColumnWidth(
        pageItems.map((i) => i.invoiceNumber ?? t('invoiceDetail.draftLabel')),
        '600 13.5px',
        60,
        180
      ),
    [pageItems, t]
  );
  const desktopColumns: [string, number, ('right' | 'center' | undefined)?][] = [
    ['invoices.colNumber', numberColWidth],
    ['invoices.colContractObjectPartner', 240],
    ['invoices.colDate', INVOICE_DATE_COL_WIDTH],
    ['invoices.colPaymentDate', INVOICE_DATE_COL_WIDTH],
    ['invoices.colRemaining', 105, 'right'],
    ['invoices.colTotal', 125, 'right'],
    ['invoices.colStatus', TABLE_STATUS_COL_WIDTH, 'center']
  ];
  const tableMinWidthPx = tableMinWidth(
    [...desktopColumns.map(([, width]) => width), TABLE_ACTION_COL_WIDTH],
    20
  );
  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const { data: objectsData } = useListObjects();
  const objectOptions = useMemo(
    () =>
      (objectsData ?? [])
        .filter((object) => object.isActive)
        .map((object) => ({
          value: object.id ?? '',
          label: formatObjectLabel(object)
        }))
        .filter((option) => option.value),
    [objectsData, t]
  );

  const { data: connectionsData } = useListOrganisationConnections();
  const counterpartyOptions = useMemo(
    () =>
      (connectionsData ?? [])
        .filter((c) => c.isActive)
        .map((c) => {
          const isOrgA = c.orgAId === user?.organisationId;
          return {
            value: (isOrgA ? c.orgBId : c.orgAId) ?? '',
            label: (isOrgA ? c.orgBName : c.orgAName) ?? '—'
          };
        })
        .filter((option) => option.value),
    [connectionsData, user?.organisationId]
  );

  // Kontrakti se i dalje dohvaćaju u punom obimu — samo za razrješavanje
  // naziva druge strane po retku, ne za pretragu (ta ide na backend preko /search).
  const { data: contractsData } = useListContracts();
  const contracts: Contract[] = contractsData ?? [];
  const contractMap = useMemo(() => {
    const map = new Map<string, Contract>();
    contracts.forEach((contract) => {
      if (contract.id) map.set(contract.id, contract);
    });
    return map;
  }, [contracts]);

  const statusOptions = [
    { value: 'ALL', label: t('invoices.allStatuses') },
    ...Object.values(InvoiceStatus).map((status) => ({
      value: status,
      label: t(statusConfig[status].labelKey)
    }))
  ];

  const createInvoiceButton = (mobile?: boolean) =>
    mobile ? (
      <MobileFab onClick={() => setCreateModalOpened(true)}>{t('invoices.create')}</MobileFab>
    ) : (
      <Button onClick={() => setCreateModalOpened(true)}>{t('invoices.create')}</Button>
    );

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  return (
    <Container size="lg" px={0}>
      <Stack gap={18}>
        {savedMessage && (
          <StatusBanner
            tone="success"
            message={savedMessage}
            onDismiss={() => setSavedMessage(null)}
          />
        )}
        <Group justify="space-between" wrap="nowrap" align="flex-start" visibleFrom="sm">
          <HeaderIntro />
          {createInvoiceButton()}
        </Group>
        <Stack gap={12} hiddenFrom="sm">
          <HeaderIntro />
          {createInvoiceButton(true)}
        </Stack>

        <Box hiddenFrom="sm">
          <MobileListFilters
            search={
              <SearchField
                hideLabel
                placeholder={t('invoices.searchPlaceholder')}
                value={search}
                onChange={(event) => handleSearchChange(event.currentTarget.value)}
              />
            }
            filters={[
              {
                key: 'status',
                label: t('common.filters.status'),
                options: statusOptions,
                value: statusFilter,
                onChange: (value) => setStatusFilter(value ?? 'ALL'),
                emptyValue: 'ALL',
                variant: 'buttons'
              },
              {
                key: 'counterparty',
                label: t('invoices.filterCounterparty'),
                options: counterpartyOptions,
                value: counterpartyFilter,
                onChange: setCounterpartyFilter
              },
              {
                key: 'object',
                label: t('invoices.filterObject'),
                options: objectOptions,
                value: objectFilter,
                onChange: setObjectFilter
              }
            ]}
          />
        </Box>

        <Group gap={16} align="flex-end" wrap="wrap" visibleFrom="sm">
          <Box w={280}>
            <SearchField
              hideLabel
              placeholder={t('invoices.searchPlaceholder')}
              value={search}
              onChange={(event) => handleSearchChange(event.currentTarget.value)}
            />
          </Box>
          <Box w={220}>
            <SelectField
              hideLabel
              data={statusOptions}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value ?? 'ALL')}
              allowDeselect={false}
            />
          </Box>
          <Box w={200}>
            <SelectField
              hideLabel
              placeholder={t('invoices.filterCounterparty')}
              data={counterpartyOptions}
              value={counterpartyFilter}
              onChange={setCounterpartyFilter}
              clearable
            />
          </Box>
          <Box w={200}>
            <SelectField
              hideLabel
              placeholder={t('invoices.filterObject')}
              data={objectOptions}
              value={objectFilter}
              onChange={setObjectFilter}
              clearable
            />
          </Box>
        </Group>

        <Box visibleFrom="sm">
          <CardSection>
            {!isLoading && pageItems.length === 0 && (
              <Center py={40}>
                <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                  {t('invoices.emptyState')}
                </Text>
              </Center>
            )}

            {pageItems.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: tableMinWidthPx }}>
                  <div
                    className="table-header-row"
                    style={{
                      ...tableRowStyle,
                      padding: '12px 20px',
                      backgroundColor: 'var(--color-bg-canvas)',
                      borderBottom: '1px solid var(--color-border-soft)'
                    }}
                  >
                    {desktopColumns.map(([key, width, align]) => (
                      <Text
                        key={key}
                        fz={10}
                        fw={600}
                        w={width}
                        ta={align}
                        style={{
                          color: 'var(--color-text-muted)',
                          letterSpacing: 0.4,
                          whiteSpace: 'pre-line'
                        }}
                      >
                        {t(key)}
                      </Text>
                    ))}
                    <Text
                      fz={10}
                      fw={600}
                      w={TABLE_ACTION_COL_WIDTH}
                      style={{
                        ...tableCenteredCellStyle,
                        color: 'var(--color-text-muted)',
                        letterSpacing: 0.4
                      }}
                    >
                      {t('invoices.colAction')}
                    </Text>
                  </div>

                  {pageItems.map((invoice, index) => {
                    const contract = invoice.contractId
                      ? contractMap.get(invoice.contractId)
                      : undefined;
                    const config = statusConfig[invoice.status ?? InvoiceStatus.DRAFT];
                    const remaining = (invoice.totalAmount ?? 0) - (invoice.paidAmount ?? 0);

                    return (
                      <div
                        key={invoice.id}
                        style={{
                          ...tableRowStyle,
                          padding: '14px 20px',
                          borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                        }}
                      >
                        <Text
                          fz={13.5}
                          fw={600}
                          w={numberColWidth}
                          style={{ color: 'var(--color-text-primary)' }}
                        >
                          {invoice.invoiceNumber ?? t('invoiceDetail.draftLabel')}
                        </Text>
                        <Stack
                          gap={1}
                          w={240}
                          style={{ cursor: invoice.contractId ? 'pointer' : undefined }}
                          role={invoice.contractId ? 'button' : undefined}
                          tabIndex={invoice.contractId ? 0 : undefined}
                          onClick={() =>
                            invoice.contractId && navigate(`/contracts/${invoice.contractId}`)
                          }
                          onKeyDown={(event) => {
                            if ((event.key === 'Enter' || event.key === ' ') && invoice.contractId)
                              navigate(`/contracts/${invoice.contractId}`);
                          }}
                        >
                          <Text
                            fz={13.5}
                            fw={600}
                            truncate
                            style={{ color: 'var(--color-text-primary)' }}
                          >
                            {invoice.contractNumber ?? '—'}
                          </Text>
                          <Text fz={11.5} truncate style={{ color: 'var(--color-text-muted)' }}>
                            {[
                              invoice.objectName,
                              contract ? otherPartyName(contract, user?.orgName) : undefined
                            ]
                              .filter(Boolean)
                              .join(' · ') || '—'}
                          </Text>
                        </Stack>
                        <Text
                          fz={13}
                          w={INVOICE_DATE_COL_WIDTH}
                          style={{ color: 'var(--color-text-muted)' }}
                        >
                          {formatDate(invoice.issueDate)}
                        </Text>
                        <Text
                          fz={13}
                          w={INVOICE_DATE_COL_WIDTH}
                          style={{ color: 'var(--color-text-muted)' }}
                        >
                          {formatDate(invoice.lastPaymentDate)}
                        </Text>
                        <Text
                          fz={13.5}
                          fw={600}
                          w={105}
                          ta="right"
                          style={{ color: 'var(--color-text-primary)' }}
                        >
                          {formatAmount(remaining)}
                        </Text>
                        <div style={{ width: 125 }}>
                          <InvoiceAmountCell
                            subtotal={invoice.subtotal}
                            taxAmount={invoice.taxAmount}
                            totalAmount={invoice.totalAmount}
                            align="right"
                          />
                        </div>
                        <div style={{ ...tableCenteredCellStyle, width: TABLE_STATUS_COL_WIDTH }}>
                          <StatusPill label={t(config.labelKey)} bg={config.bg} fg={config.fg} />
                        </div>
                        <Group w={TABLE_ACTION_COL_WIDTH} justify="center">
                          <RowActionsMenu
                            detailsHref={`/invoices/${invoice.id}`}
                            historyHref={`/invoices/${invoice.id}/history`}
                            onPreview={() => handlePreviewInvoice(invoice.id)}
                          />
                        </Group>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {pageItems.length > 0 && (
              <ListPagination
                label={t('invoices.showing', {
                  from: totalElements === 0 ? 0 : (page - 1) * pageSize + 1,
                  to: Math.min(page * pageSize, totalElements),
                  total: totalElements
                })}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
              />
            )}
          </CardSection>
        </Box>

        <Stack hiddenFrom="sm" gap={12}>
          {!isLoading && pageItems.length === 0 && (
            <Center py={40}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('invoices.emptyState')}
              </Text>
            </Center>
          )}

          {pageItems.length > 0 && (
            <>
              {pageItems.map((invoice) => {
                const contract = invoice.contractId
                  ? contractMap.get(invoice.contractId)
                  : undefined;
                const config = statusConfig[invoice.status ?? InvoiceStatus.DRAFT];
                const remaining = (invoice.totalAmount ?? 0) - (invoice.paidAmount ?? 0);

                const partnerField: [string, string, boolean?] = [
                  'invoices.colPartner',
                  contract ? otherPartyName(contract, user?.orgName) : '—'
                ];
                const fieldsAfterLinks: [string, string, boolean?][] = [
                  ['invoices.colDate', formatDate(invoice.issueDate)],
                  ['invoices.colPaymentDate', formatDate(invoice.lastPaymentDate)],
                  ['invoices.colRemaining', formatAmount(remaining), true]
                ];

                return (
                  <Stack
                    key={invoice.id}
                    gap={10}
                    p={16}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/invoices/${invoice.id}`)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        navigate(`/invoices/${invoice.id}`);
                      }
                    }}
                    style={{
                      borderRadius: 12,
                      border: '1px solid var(--color-border-soft)',
                      backgroundColor: 'var(--color-bg-surface)',
                      cursor: 'pointer'
                    }}
                  >
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                      <Text fz={14} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                        {invoice.invoiceNumber ?? t('invoiceDetail.draftLabel')}
                      </Text>
                      <Box onClick={(event) => event.stopPropagation()}>
                        <RowActionsMenu
                          detailsHref={`/invoices/${invoice.id}`}
                          historyHref={`/invoices/${invoice.id}/history`}
                          onPreview={() => handlePreviewInvoice(invoice.id)}
                        />
                      </Box>
                    </Group>

                    <Stack gap={4}>
                      <Group justify="space-between" wrap="nowrap" gap={8}>
                        <Text
                          fz={10.5}
                          fw={600}
                          style={{ color: 'var(--color-text-muted)', letterSpacing: 0.3 }}
                        >
                          {t(partnerField[0])}
                        </Text>
                        <Text fz={13} ta="right" style={{ color: 'var(--color-text-primary)' }}>
                          {partnerField[1]}
                        </Text>
                      </Group>
                      <Group justify="space-between" wrap="nowrap" gap={8}>
                        <Text
                          fz={10.5}
                          fw={600}
                          style={{ color: 'var(--color-text-muted)', letterSpacing: 0.3 }}
                        >
                          {t('invoices.colObject')}
                        </Text>
                        <Text
                          fz={13}
                          ta="right"
                          style={{
                            color: 'var(--color-text-primary)',
                            cursor: invoice.objectId ? 'pointer' : undefined
                          }}
                          role={invoice.objectId ? 'button' : undefined}
                          tabIndex={invoice.objectId ? 0 : undefined}
                          onClick={(event) => {
                            if (!invoice.objectId) return;
                            event.stopPropagation();
                            navigate(`/templates/object-registry/${invoice.objectId}`);
                          }}
                          onKeyDown={(event) => {
                            if ((event.key === 'Enter' || event.key === ' ') && invoice.objectId) {
                              event.stopPropagation();
                              navigate(`/templates/object-registry/${invoice.objectId}`);
                            }
                          }}
                        >
                          {invoice.objectName ?? '—'}
                        </Text>
                      </Group>
                      <Group justify="space-between" wrap="nowrap" gap={8}>
                        <Text
                          fz={10.5}
                          fw={600}
                          style={{ color: 'var(--color-text-muted)', letterSpacing: 0.3 }}
                        >
                          {t('invoices.colContract')}
                        </Text>
                        <Text
                          fz={13}
                          ta="right"
                          style={{
                            color: 'var(--color-text-primary)',
                            cursor: invoice.contractId ? 'pointer' : undefined
                          }}
                          role={invoice.contractId ? 'button' : undefined}
                          tabIndex={invoice.contractId ? 0 : undefined}
                          onClick={(event) => {
                            if (!invoice.contractId) return;
                            event.stopPropagation();
                            navigate(`/contracts/${invoice.contractId}`);
                          }}
                          onKeyDown={(event) => {
                            if (
                              (event.key === 'Enter' || event.key === ' ') &&
                              invoice.contractId
                            ) {
                              event.stopPropagation();
                              navigate(`/contracts/${invoice.contractId}`);
                            }
                          }}
                        >
                          {invoice.contractNumber ?? '—'}
                        </Text>
                      </Group>
                      {fieldsAfterLinks.map(([labelKey, value, emphasize]) => (
                        <Group key={labelKey} justify="space-between" wrap="nowrap" gap={8}>
                          <Text
                            fz={10.5}
                            fw={600}
                            style={{ color: 'var(--color-text-muted)', letterSpacing: 0.3 }}
                          >
                            {t(labelKey)}
                          </Text>
                          <Text
                            fz={emphasize ? 13.5 : 13}
                            fw={emphasize ? 700 : 400}
                            ta="right"
                            style={{ color: 'var(--color-text-primary)' }}
                          >
                            {value}
                          </Text>
                        </Group>
                      ))}

                      <Group justify="space-between" wrap="nowrap" gap={8} align="flex-start">
                        <Text
                          fz={10.5}
                          fw={600}
                          pt={2}
                          style={{ color: 'var(--color-text-muted)', letterSpacing: 0.3 }}
                        >
                          {t('invoices.colTotal')}
                        </Text>
                        <InvoiceAmountCell
                          subtotal={invoice.subtotal}
                          taxAmount={invoice.taxAmount}
                          totalAmount={invoice.totalAmount}
                          align="right"
                        />
                      </Group>
                    </Stack>

                    <StatusPill label={t(config.labelKey)} bg={config.bg} fg={config.fg} />
                  </Stack>
                );
              })}

              <ListPagination
                label={t('invoices.showing', {
                  from: totalElements === 0 ? 0 : (page - 1) * pageSize + 1,
                  to: Math.min(page * pageSize, totalElements),
                  total: totalElements
                })}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                bordered={false}
              />
            </>
          )}
        </Stack>
      </Stack>

      <CreateInvoiceModal opened={createModalOpened} onClose={() => setCreateModalOpened(false)} />
    </Container>
  );
};
